/**
 * Geometry, hit-testing and SVG path construction for v2 annotations.
 *
 * Everything here is pure: no React, no react-native, no module state. The
 * read-only half is marked `'worklet'` so the editor can hit-test and rebuild
 * paths on the UI thread during a gesture — that is what lets a drag start
 * without a round trip to JS, and it is the fix for the stale-closure
 * PanResponder the old canvas worked around by remounting on every tool change.
 *
 * Two conventions worth knowing before reading:
 *
 * - A `FitRect` is where the photo actually lands on screen under a `contain`
 *   fit: origin plus size, letterboxed. Normalized item coordinates are
 *   projected through it, so `annotationGeometry` never needs to know about
 *   zoom, pan or orientation — the caller folds all of that into the rect.
 * - Hit tolerances are in **screen points**, not normalized units. A touch
 *   target has to stay ~22pt whatever the zoom level, and normalized
 *   tolerances would also be anisotropic on a non-square photo.
 */

import {
  ARROW_HEAD_MAX_SHAFT_FRACTION,
  ARROW_HEAD_MIN_RATIO,
  ARROW_HEAD_RATIO,
  ARROW_WING_ANGLE,
  MIN_FONT_PX,
  MIN_STROKE_PX,
  TEXT_ADVANCE_RATIO,
  TEXT_ASCENT_RATIO,
  TEXT_DESCENT_RATIO,
  clamp01,
  type AnnotationItem,
  type ArrowItem,
  type EllipseItem,
  type FreehandItem,
  type NPoint,
  type TextItem,
} from './annotationSchema';

/** Where the photo is drawn on screen, in screen points. */
export interface FitRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Where a photo of `aspect` lands inside a `boxW x boxH` container under a
 * `contain` fit — the whole photo visible, letterboxed on the short axis.
 *
 * `contain`, not `cover`: the old canvas cropped the photo to a 4:3 box, so
 * anything a rep wanted to mark near an edge was simply not on screen. Under
 * contain every pixel of the photo is reachable, and the letterbox is dead
 * space that annotations can never occupy.
 *
 * A null aspect means the photo could not be measured; filling the box is the
 * only sane fallback and matches what the old canvas did.
 */
export function containRect(
  boxW: number,
  boxH: number,
  aspect: number | null
): FitRect {
  'worklet';
  if (!aspect || !Number.isFinite(aspect) || aspect <= 0) {
    return { x: 0, y: 0, w: boxW, h: boxH };
  }
  const boxAspect = boxH === 0 ? aspect : boxW / boxH;
  if (aspect > boxAspect) {
    // Photo is relatively wider: width-limited, letterboxed top and bottom.
    const h = boxW / aspect;
    return { x: 0, y: (boxH - h) / 2, w: boxW, h };
  }
  const w = boxH * aspect;
  return { x: (boxW - w) / 2, y: 0, w, h: boxH };
}

/** Roughly a fingertip. Applied in screen points at every zoom level. */
export const TOUCH_TOLERANCE_PT = 22;
/** Selection handles are grabbed before shape bodies, and grabbed more easily. */
export const HANDLE_TOLERANCE_PT = 24;

// --- Projection ---

export function projectX(u: number, rect: FitRect): number {
  'worklet';
  return rect.x + u * rect.w;
}

export function projectY(v: number, rect: FitRect): number {
  'worklet';
  return rect.y + v * rect.h;
}

export function unprojectX(sx: number, rect: FitRect): number {
  'worklet';
  return rect.w === 0 ? 0 : (sx - rect.x) / rect.w;
}

export function unprojectY(sy: number, rect: FitRect): number {
  'worklet';
  return rect.h === 0 ? 0 : (sy - rect.y) / rect.h;
}

export function rectDiagonal(rect: FitRect): number {
  'worklet';
  return Math.sqrt(rect.w * rect.w + rect.h * rect.h);
}

export function strokePxFor(width: number, rect: FitRect): number {
  'worklet';
  return Math.max(MIN_STROKE_PX, Math.round(width * rectDiagonal(rect)));
}

export function fontPxFor(size: number, rect: FitRect): number {
  'worklet';
  return Math.max(MIN_FONT_PX, Math.round(size * rectDiagonal(rect)));
}

// --- Primitive distance helpers ---

/**
 * Distance from a point to a line *segment* — clamped at both ends, so a touch
 * far past an arrow's tip does not register as being on the infinite line.
 */
export function distPointSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number
): number {
  'worklet';
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    return Math.sqrt((px - ax) * (px - ax) + (py - ay) * (py - ay));
  }
  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return Math.sqrt((px - cx) * (px - cx) + (py - cy) * (py - cy));
}

/**
 * How long an arrowhead should be.
 *
 * v1 used a flat `strokeWidth * 5` with no reference to the shaft, so a short
 * tap-drag produced a head longer than the arrow itself and read as a
 * starburst. Here the head is capped at a fraction of the shaft, then floored
 * so a long thin arrow does not lose its head — but the floor is itself capped
 * at half the shaft, so the fix cannot reintroduce the bug on a tiny arrow.
 */
export function arrowHeadLength(strokePx: number, shaftLenPx: number): number {
  'worklet';
  const capped = Math.min(
    ARROW_HEAD_RATIO * strokePx,
    ARROW_HEAD_MAX_SHAFT_FRACTION * shaftLenPx
  );
  const floor = Math.min(ARROW_HEAD_MIN_RATIO * strokePx, shaftLenPx * 0.5);
  return Math.max(capped, floor);
}

// --- Path construction ---

export function arrowPathD(item: ArrowItem, rect: FitRect): string {
  'worklet';
  const sx = projectX(item.from[0], rect);
  const sy = projectY(item.from[1], rect);
  let ex = projectX(item.to[0], rect);
  let ey = projectY(item.to[1], rect);

  let dx = ex - sx;
  let dy = ey - sy;
  let shaft = Math.sqrt(dx * dx + dy * dy);

  // A pure tap has no direction; v1 drew a short arrow pointing right, and
  // migrated data relies on that, so keep the same convention for live drawing.
  if (shaft === 0) {
    ex = sx + Math.max(20, rect.w * 0.05);
    ey = sy;
    dx = ex - sx;
    dy = 0;
    shaft = dx;
  }

  const strokePx = strokePxFor(item.width, rect);
  const head = arrowHeadLength(strokePx, shaft);
  const angle = Math.atan2(dy, dx);

  const w1x = ex - head * Math.cos(angle - ARROW_WING_ANGLE);
  const w1y = ey - head * Math.sin(angle - ARROW_WING_ANGLE);
  const w2x = ex - head * Math.cos(angle + ARROW_WING_ANGLE);
  const w2y = ey - head * Math.sin(angle + ARROW_WING_ANGLE);

  return `M${sx},${sy} L${ex},${ey} M${ex},${ey} L${w1x},${w1y} M${ex},${ey} L${w2x},${w2y}`;
}

export function freehandPathD(item: FreehandItem, rect: FitRect): string {
  'worklet';
  const pts = item.points;
  if (pts.length === 0) return '';

  const x0 = projectX(pts[0][0], rect);
  const y0 = projectY(pts[0][1], rect);

  // A single point has no polyline to draw. A zero-length path with a round
  // cap renders nothing in react-native-svg, so emit a degenerate segment.
  if (pts.length === 1) {
    return `M${x0},${y0} L${x0 + 0.01},${y0}`;
  }

  let d = `M${x0},${y0}`;
  for (let i = 1; i < pts.length; i += 1) {
    d += ` L${projectX(pts[i][0], rect)},${projectY(pts[i][1], rect)}`;
  }
  return d;
}

export interface EllipsePx {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
}

export function ellipsePx(item: EllipseItem, rect: FitRect): EllipsePx {
  'worklet';
  return {
    cx: projectX(item.center[0], rect),
    cy: projectY(item.center[1], rect),
    rx: Math.abs(item.rx) * rect.w,
    ry: Math.abs(item.ry) * rect.h,
  };
}

export interface TextPx {
  /** Left end of the baseline. */
  x: number;
  y: number;
  fontPx: number;
  /** Approximate advance width — see TEXT_ADVANCE_RATIO. */
  width: number;
  /** Top of the approximate hit box. */
  top: number;
  height: number;
}

export function textPx(item: TextItem, rect: FitRect): TextPx {
  'worklet';
  const fontPx = fontPxFor(item.size, rect);
  const x = projectX(item.at[0], rect);
  const y = projectY(item.at[1], rect);
  return {
    x,
    y,
    fontPx,
    width: TEXT_ADVANCE_RATIO * fontPx * Math.max(item.text.length, 1),
    top: y - TEXT_ASCENT_RATIO * fontPx,
    height: (TEXT_ASCENT_RATIO + TEXT_DESCENT_RATIO) * fontPx,
  };
}

// --- Hit testing ---

export function hitArrow(
  item: ArrowItem,
  sx: number,
  sy: number,
  rect: FitRect,
  tol: number
): boolean {
  'worklet';
  const strokePx = strokePxFor(item.width, rect);
  const reach = Math.max(tol, strokePx / 2 + 10);
  return (
    distPointSegment(
      sx,
      sy,
      projectX(item.from[0], rect),
      projectY(item.from[1], rect),
      projectX(item.to[0], rect),
      projectY(item.to[1], rect)
    ) <= reach
  );
}

export function hitEllipse(
  item: EllipseItem,
  sx: number,
  sy: number,
  rect: FitRect,
  tol: number
): boolean {
  'worklet';
  const e = ellipsePx(item, rect);
  const rx = Math.max(e.rx, 0.0001);
  const ry = Math.max(e.ry, 0.0001);
  const nx = (sx - e.cx) / rx;
  const ny = (sy - e.cy) / ry;
  const d = Math.sqrt(nx * nx + ny * ny);

  // A circle small enough that its ring is thinner than a fingertip has no
  // grabbable rim, so treat the whole interior as the target.
  if (Math.min(e.rx, e.ry) <= tol) {
    return d <= 1 || Math.abs(d - 1) * Math.min(rx, ry) <= tol;
  }
  return Math.abs(d - 1) * Math.min(rx, ry) <= tol;
}

export function hitFreehand(
  item: FreehandItem,
  sx: number,
  sy: number,
  rect: FitRect,
  tol: number
): boolean {
  'worklet';
  const pts = item.points;
  if (pts.length === 0) return false;

  const strokePx = strokePxFor(item.width, rect);
  const reach = Math.max(tol, strokePx / 2 + 10);

  // Bounding-box reject first: a 400-point stroke times N items is real work
  // to do inside a gesture handler.
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < pts.length; i += 1) {
    const x = projectX(pts[i][0], rect);
    const y = projectY(pts[i][1], rect);
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  if (sx < minX - reach || sx > maxX + reach || sy < minY - reach || sy > maxY + reach) {
    return false;
  }

  if (pts.length === 1) {
    const x = projectX(pts[0][0], rect);
    const y = projectY(pts[0][1], rect);
    return Math.sqrt((sx - x) * (sx - x) + (sy - y) * (sy - y)) <= reach;
  }

  for (let i = 1; i < pts.length; i += 1) {
    const ax = projectX(pts[i - 1][0], rect);
    const ay = projectY(pts[i - 1][1], rect);
    const bx = projectX(pts[i][0], rect);
    const by = projectY(pts[i][1], rect);
    if (distPointSegment(sx, sy, ax, ay, bx, by) <= reach) return true;
  }
  return false;
}

export function hitText(
  item: TextItem,
  sx: number,
  sy: number,
  rect: FitRect,
  tol: number
): boolean {
  'worklet';
  const t = textPx(item, rect);
  return (
    sx >= t.x - tol &&
    sx <= t.x + t.width + tol &&
    sy >= t.top - tol &&
    sy <= t.top + t.height + tol
  );
}

export function hitTestItem(
  item: AnnotationItem,
  sx: number,
  sy: number,
  rect: FitRect,
  tol: number = TOUCH_TOLERANCE_PT
): boolean {
  'worklet';
  if (item.kind === 'arrow') return hitArrow(item, sx, sy, rect, tol);
  if (item.kind === 'ellipse') return hitEllipse(item, sx, sy, rect, tol);
  if (item.kind === 'freehand') return hitFreehand(item, sx, sy, rect, tol);
  return hitText(item, sx, sy, rect, tol);
}

/** Walks back to front so the item drawn on top wins the tap. */
export function hitTestTopmost(
  items: AnnotationItem[],
  sx: number,
  sy: number,
  rect: FitRect,
  tol: number = TOUCH_TOLERANCE_PT
): string | null {
  'worklet';
  for (let i = items.length - 1; i >= 0; i -= 1) {
    if (hitTestItem(items[i], sx, sy, rect, tol)) return items[i].id;
  }
  return null;
}

// --- Selection handles ---

/**
 * Handle positions in normalized space. Ellipse corners are ordered
 * top-left, top-right, bottom-right, bottom-left, so the opposite corner of
 * handle `i` is always `(i + 2) % 4`.
 *
 * Freehand and text have no handles: reshaping a 400-point stroke by its
 * vertices is a UI nobody asked for, and text resizes through steppers.
 */
export function handlesFor(item: AnnotationItem): NPoint[] {
  if (item.kind === 'arrow') return [item.from, item.to];
  if (item.kind === 'ellipse') {
    const [cu, cv] = item.center;
    const rx = Math.abs(item.rx);
    const ry = Math.abs(item.ry);
    return [
      [cu - rx, cv - ry],
      [cu + rx, cv - ry],
      [cu + rx, cv + ry],
      [cu - rx, cv + ry],
    ];
  }
  return [];
}

export function applyHandleDrag(
  item: AnnotationItem,
  handleIndex: number,
  u: number,
  v: number
): AnnotationItem {
  const nu = clamp01(u);
  const nv = clamp01(v);

  if (item.kind === 'arrow') {
    if (handleIndex === 0) return { ...item, from: [nu, nv] };
    if (handleIndex === 1) return { ...item, to: [nu, nv] };
    return item;
  }

  if (item.kind === 'ellipse') {
    const corners = handlesFor(item);
    const fixed = corners[(handleIndex + 2) % 4];
    if (!fixed) return item;
    return {
      ...item,
      center: [(nu + fixed[0]) / 2, (nv + fixed[1]) / 2],
      rx: Math.abs(nu - fixed[0]) / 2,
      ry: Math.abs(nv - fixed[1]) / 2,
    };
  }

  return item;
}

// --- Bounds and movement ---

export interface NBounds {
  minU: number;
  minV: number;
  maxU: number;
  maxV: number;
}

/** Geometric bounds in normalized space. Ignores stroke width and text advance. */
export function boundsOf(item: AnnotationItem): NBounds {
  if (item.kind === 'arrow') {
    return {
      minU: Math.min(item.from[0], item.to[0]),
      maxU: Math.max(item.from[0], item.to[0]),
      minV: Math.min(item.from[1], item.to[1]),
      maxV: Math.max(item.from[1], item.to[1]),
    };
  }
  if (item.kind === 'ellipse') {
    const rx = Math.abs(item.rx);
    const ry = Math.abs(item.ry);
    return {
      minU: item.center[0] - rx,
      maxU: item.center[0] + rx,
      minV: item.center[1] - ry,
      maxV: item.center[1] + ry,
    };
  }
  if (item.kind === 'freehand') {
    let minU = Infinity;
    let minV = Infinity;
    let maxU = -Infinity;
    let maxV = -Infinity;
    for (let i = 0; i < item.points.length; i += 1) {
      const [u, v] = item.points[i];
      if (u < minU) minU = u;
      if (u > maxU) maxU = u;
      if (v < minV) minV = v;
      if (v > maxV) maxV = v;
    }
    if (minU === Infinity) return { minU: 0, minV: 0, maxU: 0, maxV: 0 };
    return { minU, minV, maxU, maxV };
  }
  // Text is anchored at a single point; keeping the anchor on the image is the
  // constraint that matters, since its extent depends on a font we cannot measure.
  return { minU: item.at[0], maxU: item.at[0], minV: item.at[1], maxV: item.at[1] };
}

/**
 * Translate an item, clamping the *delta* rather than each point.
 *
 * Clamping points independently would squash a shape against the edge of the
 * photo instead of stopping it, which reads as the annotation deforming under
 * your finger.
 */
export function moveItem(item: AnnotationItem, du: number, dv: number): AnnotationItem {
  const b = boundsOf(item);
  const clampedDu = Math.max(-b.minU, Math.min(du, 1 - b.maxU));
  const clampedDv = Math.max(-b.minV, Math.min(dv, 1 - b.maxV));

  if (item.kind === 'arrow') {
    return {
      ...item,
      from: [item.from[0] + clampedDu, item.from[1] + clampedDv],
      to: [item.to[0] + clampedDu, item.to[1] + clampedDv],
    };
  }
  if (item.kind === 'ellipse') {
    return {
      ...item,
      center: [item.center[0] + clampedDu, item.center[1] + clampedDv],
    };
  }
  if (item.kind === 'freehand') {
    return {
      ...item,
      points: item.points.map(
        (p): NPoint => [p[0] + clampedDu, p[1] + clampedDv]
      ),
    };
  }
  return { ...item, at: [item.at[0] + clampedDu, item.at[1] + clampedDv] };
}

// --- Freehand compression ---

/**
 * Ramer-Douglas-Peucker, iterative.
 *
 * Iterative rather than recursive because a fast drag across a large photo can
 * emit thousands of points, and a recursive implementation is a stack overflow
 * waiting for the one rep who draws a very long squiggle.
 */
export function simplifyRDP(points: NPoint[], eps: number): NPoint[] {
  if (points.length <= 2) return points.slice();

  const keep = new Uint8Array(points.length);
  keep[0] = 1;
  keep[points.length - 1] = 1;

  const stack: [number, number][] = [[0, points.length - 1]];
  while (stack.length > 0) {
    const segment = stack.pop();
    if (!segment) break;
    const [first, last] = segment;
    if (last <= first + 1) continue;

    let maxDist = -1;
    let index = -1;
    for (let i = first + 1; i < last; i += 1) {
      const d = distPointSegment(
        points[i][0],
        points[i][1],
        points[first][0],
        points[first][1],
        points[last][0],
        points[last][1]
      );
      if (d > maxDist) {
        maxDist = d;
        index = i;
      }
    }

    if (maxDist > eps && index > 0) {
      keep[index] = 1;
      stack.push([first, index]);
      stack.push([index, last]);
    }
  }

  const out: NPoint[] = [];
  for (let i = 0; i < points.length; i += 1) {
    if (keep[i]) out.push(points[i]);
  }
  return out;
}

/** Uniform stride sample, always keeping the first and last point. */
export function decimateToMax(points: NPoint[], max: number): NPoint[] {
  if (points.length <= max || max < 2) return points.slice();
  const out: NPoint[] = [];
  const stride = (points.length - 1) / (max - 1);
  for (let i = 0; i < max - 1; i += 1) {
    out.push(points[Math.round(i * stride)]);
  }
  out.push(points[points.length - 1]);
  return out;
}
